import {
  Autocomplete,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useState } from "react";
import useGeneros from "../../../hooks/useGeneros";
import countries from "../../../utils/countries";
import { MAX_GENEROS_MULTIARTISTA } from "../../../utils/tiposArtista";
import CampoPresskit from "../../../components/CampoPresskit";

const REDES = [
  { clave: "instagram", label: "Instagram" },
  { clave: "tiktok", label: "TikTok" },
  { clave: "spotify", label: "Spotify" },
  { clave: "youtube", label: "YouTube" },
  { clave: "facebook", label: "Facebook" },
  { clave: "twitter", label: "X (Twitter)" },
  { clave: "pagina_web", label: "Página web" },
];

const VACIO = {
  nombre: "",
  descripcion: "",
  pais: "",
  generos_musicales: [],
  redes: {},
};

// Alta y edición de una ficha del roster. El mismo formulario para las dos
// cosas: al editar llega `artista` y los campos arrancan con sus valores.
export default function FormArtista({
  abierto,
  artista,
  onCerrar,
  onGuardar,
  guardando,
  showSnack,
}) {
  const generos = useGeneros();
  const [form, setForm] = useState(VACIO);
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(null);
  const [presskit, setPresskit] = useState(null);
  const [quitarPresskit, setQuitarPresskit] = useState(false);
  const [generoElegido, setGeneroElegido] = useState("");

  const esEdicion = !!artista;

  // Cada vez que se abre el diálogo se recarga desde cero: si no, editar un
  // artista y después dar de alta otro arrastraría los datos del anterior.
  useEffect(() => {
    if (!abierto) return;
    setForm(
      artista
        ? {
            nombre: artista.nombre || "",
            descripcion: artista.descripcion || "",
            pais: artista.pais || "",
            generos_musicales: artista.generos_musicales || [],
            redes: artista.redes || {},
          }
        : VACIO
    );
    setImagen(null);
    setPreview(artista?.imagen || null);
    setPresskit(null);
    setQuitarPresskit(false);
    setGeneroElegido("");
  }, [abierto, artista]);

  const cambiar = (campo) => (e) =>
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const cambiarRed = (clave) => (e) =>
    setForm((prev) => ({
      ...prev,
      redes: { ...prev.redes, [clave]: e.target.value },
    }));

  const agregarGenero = () => {
    if (!generoElegido) {
      showSnack("No has elegido ningún género.", "warning");
      return;
    }
    if (form.generos_musicales.includes(generoElegido)) {
      showSnack("Ya agregaste este género.", "warning");
      return;
    }
    if (form.generos_musicales.length >= MAX_GENEROS_MULTIARTISTA) {
      showSnack(`Puedes agregar hasta ${MAX_GENEROS_MULTIARTISTA} géneros.`, "warning");
      return;
    }
    setForm((prev) => ({
      ...prev,
      generos_musicales: [...prev.generos_musicales, generoElegido],
    }));
    setGeneroElegido("");
  };

  const quitarGenero = (genero) => () =>
    setForm((prev) => ({
      ...prev,
      generos_musicales: prev.generos_musicales.filter((g) => g !== genero),
    }));

  const elegirImagen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagen(file);
    setPreview(URL.createObjectURL(file));
  };

  const guardar = () => {
    if (!form.nombre.trim()) {
      showSnack("El nombre del artista es obligatorio.", "warning");
      return;
    }
    onGuardar(
      { ...form, nombre: form.nombre.trim() },
      imagen,
      presskit,
      quitarPresskit
    );
  };

  const opcionesPais = Object.entries(countries).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="sm">
      <DialogTitle>
        {esEdicion ? `Editar a ${artista.nombre}` : "Agregar un artista"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} pt={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={preview || undefined}
              sx={{ width: 72, height: 72 }}
              variant="rounded"
            >
              {form.nombre?.[0]?.toUpperCase() || "?"}
            </Avatar>
            <Stack spacing={0.5}>
              <Button component="label" size="small">
                {preview ? "Cambiar foto" : "Subir foto"}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={elegirImagen}
                />
              </Button>
              <Typography variant="caption" color="text.secondary">
                JPG, PNG o WEBP. Se ajusta sin recortarse.
              </Typography>
            </Stack>
          </Stack>

          <TextField
            label="Nombre del artista*"
            value={form.nombre}
            onChange={cambiar("nombre")}
            fullWidth
          />

          <TextField
            label="Descripción"
            value={form.descripcion}
            onChange={cambiar("descripcion")}
            multiline
            rows={3}
            fullWidth
          />

          <Autocomplete
            options={opcionesPais}
            value={
              opcionesPais.find((opcion) => opcion.value === form.pais) || null
            }
            onChange={(e, opcion) =>
              setForm((prev) => ({ ...prev, pais: opcion?.value || "" }))
            }
            renderInput={(params) => <TextField {...params} label="País" />}
          />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Géneros musicales (hasta {MAX_GENEROS_MULTIARTISTA})
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Autocomplete
                disablePortal
                options={generos}
                value={generoElegido || null}
                onChange={(e, valor) => setGeneroElegido(valor || "")}
                fullWidth
                renderInput={(params) => <TextField {...params} />}
              />
              <Button onClick={agregarGenero}>Agregar</Button>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {form.generos_musicales.map((genero) => (
                <Chip
                  key={genero}
                  label={genero}
                  onDelete={quitarGenero(genero)}
                  size="small"
                />
              ))}
            </Stack>
          </Stack>

          <CampoPresskit
            valor={artista?.presskit}
            archivo={presskit}
            quitar={quitarPresskit}
            onElegir={(file) => {
              setPresskit(file);
              if (file) setQuitarPresskit(false);
            }}
            onQuitar={() => {
              setQuitarPresskit(true);
              setPresskit(null);
            }}
            onDeshacer={() => setQuitarPresskit(false)}
            showSnack={showSnack}
          />

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Redes y web del artista
            </Typography>
            {REDES.map(({ clave, label }) => (
              <TextField
                key={clave}
                label={label}
                size="small"
                value={form.redes?.[clave] || ""}
                onChange={cambiarRed(clave)}
                fullWidth
              />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cancelar</Button>
        <LoadingButton loading={guardando} variant="contained" onClick={guardar}>
          {esEdicion ? "Guardar cambios" : "Agregar artista"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
